/**
 * - Added `useSkipParsing` hook
 */

import { CatalogEntryRecommendation } from '@/components/content/CatalogEntryRecommendation';
import { CategoryRecommendation } from '@/components/content/CategoryRecommendation';
import { ContentCarousel } from '@/components/content/ContentCarousel';
import { ContentRecommendation } from '@/components/content/ContentRecommendation';
import { EspotSkeleton } from '@/components/skeleton/components/Espot';
import { useEMarketingSpot } from '@/data/Content/EMarketingSpot';
import { WidgetComponentProps, WidgetProperties } from '@/data/types/Slot';
import { Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FC } from 'react';
import { useSkipParsing } from '@/components/content/EMarketingSpot/hooks/useSkipParsing';

const emptyProperties = {} as WidgetProperties;

export const EMarketingSpot: FC<WidgetComponentProps> = ({
	id,
	properties = emptyProperties,
	sx,
}) => {
	const { emsName = '' } = properties;
	const { data, loading } = useEMarketingSpot(emsName);
	const {
		dimensions: { contentSpacing },
	} = useTheme();
	const skipParsing = useSkipParsing({ emsName });

	const isVisible =
		!!data.hasContent || !!data.hasCategory || !!data.hasCatEntry || !!data.hasContentCarousel;
	const contentCount = [data.hasContent, data.hasCategory, data.hasCatEntry].filter(Boolean).length;

	if (loading) {
		return <EspotSkeleton emsName={emsName} sx={sx} />;
	}

	if (!isVisible) {
		return null;
	}

	const renderContent = () => (
		<>
			{data.hasContent ? (
				data.hasContentCarousel ? (
					<ContentCarousel id={id} properties={properties} skipParsing={skipParsing} />
				) : (
					<ContentRecommendation
						id={id}
						properties={properties}
						skipParsing={skipParsing}
						sx={sx}
					/>
				)
			) : null}
			{data.hasCategory ? (
				<CategoryRecommendation id={id} properties={properties} skipParsing={skipParsing} />
			) : null}
			{data.hasCatEntry ? <CatalogEntryRecommendation id={id} properties={properties} /> : null}
		</>
	);

	if (contentCount > 1) {
		return <Stack gap={contentSpacing}>{renderContent()}</Stack>;
	}

	return <>{renderContent()}</>;
};
